import React from "react";
import { Box, IconButton } from "@mui/joy";
import { CloseIcon } from "../../../styles/StyledMUI";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import AddCriteriaForm from "./AddCriteriaForm";
import ActiveCriteria from "./ActiveCriteria";
import type { EquipmentModel, FilterCriteria } from "../../../api/equipment/Equipment.typed";

interface EquipmentsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedModel: EquipmentModel | undefined;
  searchCriteria: FilterCriteria[];
  searchLogic: "AND" | "OR";
  onAddCriteria: (criteria: FilterCriteria) => void;
  onRemoveCriteria: (index: number) => void;
  onResetAll: () => void;
  onSearchLogicChange: (logic: "AND" | "OR") => void;
  alertName?: string;
  onClearAlertName?: () => void;
}

function EquipmentsFilterDrawer({
  open,
  onClose,
  selectedModel,
  searchCriteria,
  searchLogic,
  onAddCriteria,
  onRemoveCriteria,
  onResetAll,
  onSearchLogicChange,
  alertName,
  onClearAlertName,
}: EquipmentsFilterDrawerProps) {
  if (!open) return null;

  const activeCount = searchCriteria.length;
  const hasActive = activeCount > 0 || !!alertName;

  return (
    <Box
      role="region"
      aria-label="Filtres avancés"
      sx={{
        // Overlay on mobile (slides over cards), in-flow column on desktop.
        position: { xs: "absolute", sm: "static" },
        inset: { xs: 0, sm: "auto" },
        zIndex: { xs: 5, sm: "auto" },
        width: { xs: "100%", sm: 320 },
        minWidth: { sm: 320 },
        maxWidth: { xs: "100%", sm: 320 },
        height: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: { sm: `1px solid ${colorTokens.border.default}` },
        bgcolor: colorTokens.bg.surface,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${colorTokens.border.default}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DarkTypography level="title-md" colorVariant="title" sx={{ fontWeight: 700 }}>
            Filtres
          </DarkTypography>
          {activeCount > 0 && (
            <DarkTypography
              level="body-xs"
              component="span"
              sx={{
                px: 1,
                py: 0.2,
                borderRadius: 999,
                fontWeight: 700,
                bgcolor: withAlpha(colorTokens.accent.primary, 0.15),
                border: `1px solid ${colorTokens.accent.primary}`,
                color: colorTokens.accent.primary,
              }}
            >
              {activeCount}
            </DarkTypography>
          )}
        </Box>
        <IconButton
          variant="plain"
          size="sm"
          onClick={onClose}
          aria-label="Fermer le panneau de filtres"
          sx={{
            color: colorTokens.text.muted,
            "&:hover": { bgcolor: withAlpha(colorTokens.text.primary, 0.08) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        {!selectedModel ? (
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ py: 2, textAlign: "center" }}>
            Sélectionnez d'abord un type d'équipement.
          </DarkTypography>
        ) : (
          <>
            <AddCriteriaForm selectedModel={selectedModel} onAddCriteria={onAddCriteria} />

            {hasActive && (
              <ActiveCriteria
                selectedModel={selectedModel}
                searchCriteria={searchCriteria}
                searchLogic={searchLogic}
                onRemoveCriteria={onRemoveCriteria}
                onResetAll={onResetAll}
                onSearchLogicChange={onSearchLogicChange}
                alertName={alertName}
                onClearAlertName={onClearAlertName}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default React.memo(EquipmentsFilterDrawer);
